import { useState, useEffect } from 'react'
import { collection, query, where, onSnapshot, orderBy, Timestamp, limit, doc, getDoc } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Chat, ChatStatus } from '@/types/models'
import type { Booking } from '@/types/booking'
import { fetchUserById, type ChatUserInfo } from '@/lib/chatData'
import { generateDemoChats } from '@/lib/demoBookingData'
import { useBookingSystem } from '@/contexts/BookingSystemContext'
import { 
  loadBookingDashboardCache, 
  saveBookingDashboardCache,
  type CachedChatData 
} from '@/lib/bookingDashboardCache'

export interface ChatWithUserInfo extends Chat {
  userInfo?: ChatUserInfo
  hasBooking?: boolean
  booking?: Booking
}

export interface BookingDashboardData {
  statusCounts: {
    booked: number
    artist_message_needed: number
    waiting_for_client: number
  }
  upcomingBooking: Booking | null
  chats: ChatWithUserInfo[]
  loading: boolean
  error: Error | null
}

export function useArtistBookingDashboard(artistId: string | undefined) {
  // Load cache synchronously on initialization for instant display
  const [statusCounts, setStatusCounts] = useState(() => {
    if (!artistId) return { booked: 0, artist_message_needed: 0, waiting_for_client: 0 }
    const cached = loadBookingDashboardCache(artistId)
    return cached?.statusCounts || { booked: 0, artist_message_needed: 0, waiting_for_client: 0 }
  })
  
  const [upcomingBooking, setUpcomingBooking] = useState<Booking | null>(() => {
    if (!artistId) return null
    const cached = loadBookingDashboardCache(artistId)
    return cached?.upcomingBooking || null
  })
  
  const [chats, setChats] = useState<ChatWithUserInfo[]>(() => {
    if (!artistId) return []
    const cached = loadBookingDashboardCache(artistId)
    if (cached && cached.chats.length > 0) {
      console.log('[useArtistBookingDashboard] ⚡ Initial cached data loaded:', cached.chats.length, 'chats')
      // Convert cached data back to ChatWithUserInfo format
      return cached.chats.map(cachedChat => ({
        id: cachedChat.chatId,
        userId: cachedChat.userId,
        artistId: cachedChat.artistId,
        flashId: null,
        createdAt: new Date(cachedChat.lastMessageAt),
        lastMessageAt: new Date(cachedChat.lastMessageAt),
        lastMessage: cachedChat.lastMessageContent,
        status: cachedChat.status,
        bookingId: cachedChat.bookingId || undefined,
        hasBooking: cachedChat.hasBooking,
        userInfo: cachedChat.userInfo,
        booking: cachedChat.booking,
        messages: []
      }))
    }
    return []
  })
  
  const [loading, setLoading] = useState(() => {
    // If we have cached data, don't show loading state
    if (!artistId) return true
    const cached = loadBookingDashboardCache(artistId)
    return !cached || cached.chats.length === 0
  })
  
  const [error, setError] = useState<Error | null>(null)
  const [isShowingCachedData, setIsShowingCachedData] = useState(() => {
    if (!artistId) return false
    const cached = loadBookingDashboardCache(artistId)
    const hasCache = !!(cached && cached.chats.length > 0)
    if (hasCache) {
      console.log('[useArtistBookingDashboard] ⚡ Showing cached data instantly (no flicker!)')
    }
    return hasCache
  })

  // Get subscription data from BookingSystem context to check if we should include fake chats
  const { subscription } = useBookingSystem()
  const includeFakeChats = subscription?.includeFakeChats ?? false

  useEffect(() => {
    if (!artistId) {
      setLoading(false)
      return
    }

    // If includeFakeChats is enabled, we'll mix demo and real chats later
    // For now, continue to load real chats from Firestore

    try {
      // Listen to chats for this artist
      const chatsRef = collection(db, 'chats')
      const chatsQuery = query(
        chatsRef,
        where('artistId', '==', artistId),
        orderBy('lastMessageAt', 'desc')
      )

      const unsubscribeChats = onSnapshot(
        chatsQuery,
        async (snapshot) => {
          const counts = {
            booked: 0,
            artist_message_needed: 0,
            waiting_for_client: 0
          }

          // Step 1: Process all chat documents into chat data (no async operations yet)
          const chatsData: ChatWithUserInfo[] = snapshot.docs.map(chatDoc => {
            const data = chatDoc.data()
            const hasBooking = !!data.bookingId
            
            // Count status
            if (hasBooking) counts.booked++
            if ((data.status || 'active') === 'artist_message_needed') counts.artist_message_needed++
            else if ((data.status || 'active') === 'waiting_for_client') counts.waiting_for_client++

            return {
              id: chatDoc.id,
              userId: data.userId,
              artistId: data.artistId,
              flashId: data.flashId || null,
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
              lastMessageAt: data.lastMessageAt instanceof Timestamp ? data.lastMessageAt.toDate() : new Date(data.lastMessageAt),
              lastMessage: data.lastMessage || '',
              status: (data.status || 'active') as ChatStatus,
              bookingId: data.bookingId,
              hasBooking: hasBooking,
              messages: (data.messages || []).map((msg: any) => ({
                id: msg.id || '',
                content: msg.content || '',
                senderId: msg.senderId || '',
                senderType: msg.senderType || 'user',
                timestamp: msg.timestamp instanceof Timestamp ? msg.timestamp.toDate() : new Date(msg.timestamp),
                type: msg.type || 'text',
                isRead: msg.isRead !== undefined ? msg.isRead : true,
                imageUrl: msg.imageUrl,
                imageUrls: msg.imageUrls
              }))
            }
          })

          // Step 2: Get unique user IDs and fetch all user info in PARALLEL
          const uniqueUserIds = [...new Set(chatsData.map(chat => chat.userId))]
          const userInfoResults = await Promise.all(
            uniqueUserIds.map(userId => fetchUserById(userId).catch(err => {
              console.error(`Error fetching user info for user ${userId}:`, err)
              return null
            }))
          )
          
          // Create a map for quick lookup
          const userInfoMap = new Map<string, ChatUserInfo | null>()
          uniqueUserIds.forEach((userId, index) => {
            userInfoMap.set(userId, userInfoResults[index])
          })

          // Step 3: Get booking IDs and fetch all bookings in PARALLEL using direct document fetch
          const bookingIds = chatsData
            .filter(chat => chat.bookingId)
            .map(chat => chat.bookingId as string)
          
          const bookingResults = await Promise.all(
            bookingIds.map(bookingId => 
              getDoc(doc(db, 'bookings', bookingId)).catch(err => {
                console.error(`Error fetching booking ${bookingId}:`, err)
                return null
              })
            )
          )
          
          // Create a map for quick lookup
          const bookingMap = new Map<string, Booking | null>()
          bookingIds.forEach((bookingId, index) => {
            const bookingSnapshot = bookingResults[index]
            if (bookingSnapshot && bookingSnapshot.exists()) {
              const data = bookingSnapshot.data()
              const clientUsername = data.clientData?.Username || data.clientUsername || 'Unknown'
              bookingMap.set(bookingId, {
                id: bookingSnapshot.id,
                chatId: data.chatId,
                userId: data.userId,
                artistId: data.artistId,
                clientUsername: clientUsername,
                type: data.type,
                shortSummary: data.shortSummary || '',
                flashId: data.flashId,
                flashImageUrl: data.flashImageUrl,
                customImageUrl: data.customImageUrl,
                customDesignStatus: data.customDesignStatus,
                scheduledTimestamp: data.scheduledTimestamp instanceof Timestamp ? data.scheduledTimestamp.toDate() : new Date(data.scheduledTimestamp),
                createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
                updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined,
                estimatedTime: data.estimatedTime,
                googleCalendarEventId: data.googleCalendarEventId
              })
            }
          })

          // Step 4: Attach user info and booking data to chats
          chatsData.forEach(chat => {
            chat.userInfo = userInfoMap.get(chat.userId) || undefined
            if (chat.bookingId) {
              chat.booking = bookingMap.get(chat.bookingId) || undefined
            }
          })

          // Mix in demo data if includeFakeChats is enabled in subscription
          let finalChatsData = chatsData
          if (includeFakeChats) {
            const demoChats = generateDemoChats(artistId)
            finalChatsData = [...chatsData, ...demoChats]
            
            // Update counts with demo data
            demoChats.forEach(chat => {
              if (chat.hasBooking) counts.booked++
              if (chat.status === 'artist_message_needed') counts.artist_message_needed++
              else if (chat.status === 'waiting_for_client') counts.waiting_for_client++
            })
          }

          setChats(finalChatsData)
          setStatusCounts(counts)
          setLoading(false)
          
          // Transition away from cached data
          if (isShowingCachedData) {
            console.log('[useArtistBookingDashboard] ⚡ Transitioning from cached to fresh data')
            setIsShowingCachedData(false)
          }
          
          // Save fresh data to cache for next visit
          const cachedChats: CachedChatData[] = finalChatsData.map(chat => {
            const lastMessage = chat.messages?.[chat.messages.length - 1]
            let lastMessageSender = 'Unknown'
            if (lastMessage) {
              if (lastMessage.senderType === 'artist') {
                lastMessageSender = 'You'
              } else {
                lastMessageSender = chat.userInfo?.instagram || chat.userInfo?.email || 'User'
              }
            }
            
            return {
              chatId: chat.id,
              userId: chat.userId,
              artistId: chat.artistId,
              status: chat.status,
              lastMessageAt: chat.lastMessageAt.getTime(),
              displayName: chat.userInfo?.instagram || chat.userInfo?.email || chat.userInfo?.displayName || 'Unknown',
              avatarUrl: chat.userInfo?.avatarUrl || null,
              avatarInitial: ((chat.userInfo?.instagram || chat.userInfo?.email || '?').replace(/^@/, '')[0] || '?').toUpperCase(),
              userInfo: chat.userInfo,
              lastMessageSender,
              lastMessageContent: chat.lastMessage,
              isLastMessageUnread: lastMessage?.isRead === false,
              hasBooking: chat.hasBooking || false,
              bookingId: chat.bookingId,
              booking: chat.booking
            }
          })
          
          saveBookingDashboardCache(artistId, {
            statusCounts: counts,
            upcomingBooking: null, // Will be updated by the bookings subscription
            chats: cachedChats
          })
        },
        (err) => {
          console.error('[ArtistBookingDashboard] Error fetching chats:', err)
          setError(err as Error)
          setLoading(false)
          setIsShowingCachedData(false)
        }
      )

      // Listen to bookings for upcoming booking (future bookings only)
      const bookingsRef = collection(db, 'bookings')
      const now = new Date()
      const upcomingBookingsQuery = query(
        bookingsRef,
        where('artistId', '==', artistId),
        where('scheduledTimestamp', '>=', Timestamp.fromDate(now)),
        orderBy('scheduledTimestamp', 'asc'),
        limit(1)
      )

      const unsubscribeBookings = onSnapshot(
        upcomingBookingsQuery,
        (snapshot) => {
          let bookingData: Booking | null = null
          
          if (!snapshot.empty) {
            const doc = snapshot.docs[0]
            const data = doc.data()
            // Extract clientUsername from clientData.Username or fall back to direct field for backward compatibility
            const clientUsername = data.clientData?.Username || data.clientUsername || 'Unknown'
            bookingData = {
              id: doc.id,
              chatId: data.chatId,
              userId: data.userId,
              artistId: data.artistId,
              clientUsername: clientUsername,
              type: data.type,
              shortSummary: data.shortSummary || '',
              flashId: data.flashId,
              flashImageUrl: data.flashImageUrl,
              customImageUrl: data.customImageUrl,
              customDesignStatus: data.customDesignStatus,
              scheduledTimestamp: data.scheduledTimestamp instanceof Timestamp ? data.scheduledTimestamp.toDate() : new Date(data.scheduledTimestamp),
              createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
              updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : undefined
            }
          }
          
          setUpcomingBooking(bookingData)
          
          // Update cache with upcoming booking
          const currentCache = loadBookingDashboardCache(artistId)
          if (currentCache) {
            saveBookingDashboardCache(artistId, {
              ...currentCache,
              upcomingBooking: bookingData
            })
          }
        },
        (err) => {
          console.error('[ArtistBookingDashboard] Error fetching bookings:', err)
          // Don't set error here as it's optional
        }
      )

      return () => {
        unsubscribeChats()
        unsubscribeBookings()
      }
    } catch (err) {
      console.error('[ArtistBookingDashboard] Error setting up listeners:', err)
      setError(err as Error)
      setLoading(false)
    }
  }, [artistId, includeFakeChats])

  return { statusCounts, upcomingBooking, chats, loading, error }
}

